// [목적] /explore 발견 페이지 — 추천 폴리오 섹션 + 필터 모달 + 실데이터 무한스크롤
// [이유] design-align-v2: "추천 폴리오" 섹션(reason badge 포함)을 메인 그리드 위에 추가.
//         필터 버튼 클릭 시 모달(여행 기간 + 테마 멀티셀렉트 + 초기화/결과 보기) 표시.
//         기존 sort select 3종, 공식 컬렉션 섹션, 필터칩 10종, 무한스크롤 보존.
//         M7 P1(발견 화면 서버 검색·필터): 검색어·필터 모달(기간·테마)·정렬을 useExploreFilters(URL 쿼리)로
//         동기화 → useFolioFeed(q/tags/minDurationDays/maxDurationDays/sort) 서버 조회로 교체(클라이언트
//         제목/vibe 부분일치 필터 제거). FilterModal 은 components/explore/FilterModal.tsx 로 분리해
//         ExploreMobile.tsx 와 공유(제어 컴포넌트, useExploreFilters 가 SoT).
//         추천 폴리오 reason badge — mock 텍스트 제거, 실제 활성 필터(태그 일치/기간 일치) 기반으로만 생성.
//         활성 필터가 없으면(추천 근거 없음) 섹션 자체를 숨긴다.
// [사이드 이팩트] filterModalOpen state 는 유지(모달 열림 여부만 로컬), 필터 값 자체는 URL 쿼리로 이동.
//                 카테고리 칩 10종(FILTERS)도 URL 필터에서 파생·갱신(useExploreFilters CHIP_FILTERS, 2026-09-17).
// [수정 시 고려사항] 검색 입력은 300ms 디바운스 후 useExploreFilters.setQ 호출(키 입력마다 URL/서버 요청 방지).
//                   [G1-2, 2026-09-17] 공식 컬렉션 조회 실패 시 조용히 숨기지 않고 InlineErrorState(재시도)
//                   노출 — 메인 피드 에러 문구도 동일 컴포넌트로 통일(재시도 버튼 신규 추가).

import { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { TopBar } from '../components/layout/TopBar.js';
import { FolioCardSkeleton } from '../components/feedback/Skeleton.js';
import { InlineErrorState } from '../components/feedback/InlineErrorState.js';
import { FolioLikeChip } from '../components/folio/FolioLikeChip.js';
import { FilterModal } from '../components/explore/FilterModal.js';
import { useShell } from '../hooks/useShell.js';
import { useFolioFeed, type FolioFeedSort } from '../hooks/useFolioFeed.js';
import { useExploreFilters } from '../hooks/useExploreFilters.js';
import { folioAuthorLabel } from '../utils/folioAuthorLabel.js';
import * as collectionClient from '../api/collection.client.js';
import { queryKeys } from '../api/queryKeys.js';
import { ExploreMobile } from './mobile/ExploreMobile.js';
import type { Folio, Collection } from '@gangwoncanvas/types';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
// [목적] 카드·칩·입력창 등 표면 흰색 — 리터럴 '#fff' 직접 입력을 대체(CLAUDE.md §6-3 디자인 토큰 강제)
// [이유] 2026-09-20: 이 파일에만 '#fff' 리터럴이 14곳 있어 no-restricted-syntax 경고가 누적돼 있었다.
//        c.paper[0][L] 은 '#FFFFFF' 로 동일 값이라 시각적 변화 없이 경고만 제거된다.
const PAPER0 = c.paper[0][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
// [목적] 소형 브랜드 텍스트(11~14px) 전용 — EXPLORE 배지·CTA 라벨 등
// [이유] brand[500] 은 소형 텍스트에서 WCAG AA 미달(tokens/color.ts 주석) — brand[600] 을 써야 한다.
const BRAND_TEXT = c.brand[600][L];
// [목적] 섹션 구분선·개수 pill 배경 등 보조 라인 — line.base 보다 한 단계 약한 톤
const SOFT_LINE = c.line.soft[L];
// [목적] 하단 CTA 배너 그라데이션 끝 stop — 흰 텍스트를 얹는 배경 전용 진한 그린
// [이유] accent[500] 은 흰 텍스트 기준 2.61:1 로 AA 미달 — accent[600](5.40:1)을 써야 한다
//        (packages/ui tokens/color.ts accent[600] 주석, HomePage 히어로와 동일 근거).
const ACCENT_TEXT_BG = c.accent[600][L];

/** [목적] 정렬 select 3종 */
const SORT_OPTIONS: ReadonlyArray<{ id: FolioFeedSort; label: string }> = [
  { id: 'latest', label: '최신순' },
  { id: 'likes', label: '좋아요순' },
  { id: 'forks', label: 'Fork순' },
];

/**
 * [목적] 필터칩 최종 10종 — ADR-DA4 확정
 * [수정 시 고려사항] 라인업 변경 시 ExploreMobile.tsx FILTERS 와 동기 필수
 */
const FILTERS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'all', label: '전체' },
  { id: 'pet', label: '🐾 반려견' },
  { id: 'cafe', label: '☕ 카페' },
  { id: 'camp', label: '⛺ 캠핑' },
  { id: 'sea', label: '🌊 바다' },
  { id: 'mountain', label: '🏔 산' },
  { id: 'food', label: '🍜 맛집' },
  { id: 'short', label: '⏱ 당일' },
  { id: 'surf', label: '🏄 서핑' },
  { id: 'access', label: '♿ 무장애' },
];

// [목적] 공식 컬렉션 섹션 배경 색상 시퀀스
const COLLECTION_BG_CYCLE = [
  'rgba(234,244,255,1)',
  'rgba(255,244,232,1)',
  'rgba(232,248,239,1)',
  'rgba(255,240,240,1)',
  'rgba(245,240,255,1)',
  'rgba(232,240,254,1)',
] as const;

// [목적] 히어로 배경 그라데이션 — brand → paper 톤으로 흐르는 약한 틴트
// [이유] 2026-09-20 explore-visual-pass: 기존 히어로는 배경이 page 배경과 동일한 맨몸 텍스트 3줄이라
//        페이지 상단에 시선을 잡는 앱커가 없었다("비어 보임"의 주원인 중 하나).
// [사이드 이팩트] 없음(상수). 대비가 필요한 텍스트는 전부 ink 토큰을 그대로 쓴다(배경이 매우 옥아 AA 유지).
// [수정 시 고려사항] 더 진한 배경으로 바꿀 경우 히어로 내부 텍스트/칩 대비를 재측정해야 한다.
const HERO_GRADIENT = 'linear-gradient(135deg,#EEF4FE 0%,#F7F4EC 55%,#FAF9F7 100%)';

// [목적] 커버 이미지가 없는 폴리오 카드의 플레이스홀더 배경
// [이유] 기존에는 paper[2] 단색 회색 박스 + 작은 🗺 이모지만 있어 "이미지 로드 실패"처럼 보였다.
//        그라데이션 + 큰 워터마크로 바꾸면 의도된 플레이스홀더로 읽힌다.
// [사이드 이팩트] 없음(상수).
// [수정 시 고려사항] 워터마크 이모지는 opacity .22 — 더 진하게 하면 그 위에 얹히는 기간 배지 가독성이 떨어진다.
const CARD_COVER_GRADIENT = 'linear-gradient(145deg,#E3EDFB 0%,#EAF3EC 50%,#F4EFE3 100%)';

/**
 * [목적] "이런 테마는 어때요?" 탐색 타일 라인업
 * [이유] 2026-09-20 explore-visual-pass: 폴리오 수가 적을 때 피드 아래가 통째로 비어
 *        서비스 자체가 빈 것처럼 읽혔다. 탐색 진입점을 주어 아래쪽 여백을 의미 있게 채운다.
 * [사이드 이팩트] 클릭 시 exploreFilters.setChip(id) — 기존 필터칩과 완전히 동일한 경로(URL 쿼리 갱신).
 * [수정 시 고려사항] chipId 는 반드시 useExploreFilters.CHIP_FILTERS 의 키여야 한다
 *                   (없는 키면 setChip 이 필터를 전체 해제해버려 조용히 오동작한다).
 */
const THEME_TILES: ReadonlyArray<{
  chipId: string;
  emoji: string;
  label: string;
  desc: string;
  gradient: string;
}> = [
  {
    chipId: 'sea',
    emoji: '🌊',
    label: '바다',
    desc: '동해안 해변 코스',
    gradient: 'linear-gradient(140deg,#DCEBFB,#C9E2F5)',
  },
  {
    chipId: 'mountain',
    emoji: '🏔',
    label: '산',
    desc: '설악·오대산 트레킹',
    gradient: 'linear-gradient(140deg,#E3EEE4,#CFE3D4)',
  },
  {
    chipId: 'cafe',
    emoji: '☕',
    label: '카페',
    desc: '뷰 좋은 카페 투어',
    gradient: 'linear-gradient(140deg,#F5EDE0,#EADFCB)',
  },
  {
    chipId: 'camp',
    emoji: '⛺',
    label: '캠핑',
    desc: '차박·오토캠핑',
    gradient: 'linear-gradient(140deg,#E8E6F5,#D8D4EE)',
  },
  {
    chipId: 'food',
    emoji: '🍜',
    label: '맛집',
    desc: '현지인 추천 식당',
    gradient: 'linear-gradient(140deg,#FBE8DC,#F5D9C4)',
  },
  {
    chipId: 'short',
    emoji: '⏱',
    label: '당일치기',
    desc: '하루 코스',
    gradient: 'linear-gradient(140deg,#E6F1F2,#D1E6E8)',
  },
];

/**
 * [목적] 피드 그리드가 한 줄도 채우지 못할 때 빈 칸을 메꾸는 임계 개수
 * [이유] auto-fill 그리드는 항목이 적으면 오른쪽이 통째로 비는데, 이때 CTA 타일 하나를
 *        넣어 두면 "빈 칸"이 아니라 "다음 행동 유도"로 읽힌다.
 * [수정 시 고려사항] 데스크톱 그리드가 최대 4열이므로 4 — 열 수 정책이 바뀜면 함께 조정.
 */
const SPARSE_FEED_THRESHOLD = 4;

/**
 * [목적] 피드 그리드 공통 sx — 스켈레톤·실데이터 두 분기가 동일한 레이아웃을 갖도록 단일 소스화
 * [이유] 2026-09-20 explore-visual-pass — 기존은 CSS 멀티컬럼(columnCount: cols) 기반 의사 메이슨리였다.
 *        컬럼 레이아웃은 항목이 열 수보다 적으면 첫 컬럼만 채우고 나머지 열을 통째로 비워둔다
 *        — 폴리오 1개일 때 화면이 극도로 허전해 보였던 직접 원인. auto-fill 그리드는 카드 폭을
 *        고정해 어떤 개수에서도 일관된 정렬을 주고, 빈 칸은 CreateFolioTile 로 메꾸면 된다.
 * [사이드 이팩트] 카드가 더 이상 가변 높이로 엇물리지 않는다(메이슨리 포기) — ExFolioCard 의
 *                 coverH 가변 높이도 고정값으로 함께 정리되었다.
 * [수정 시 고려사항] minmax 하한(258px)을 높이면 좋은 태블릿 폭에서 열이 2로 떨어진다 —
 *                   변경 시 태블릿(768~1024px) 에서 직접 확인할 것.
 */
const FEED_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(258px, 1fr))',
  gap: '18px',
  mt: '18px',
} as const;

// ─── ExplorePage ──────────────────────────────────────────────────────────────

/**
 * [목적] /explore 페이지 진입점 — 모바일/데스크톱 분기
 * [이유] design-align-v2 추천 섹션 + 필터 모달 추가
 * [사이드 이팩트] 없음
 */
export function ExplorePage(): JSX.Element {
  const { viewport } = useShell();
  return viewport.isMobile ? <ExploreMobile /> : <ExploreDesktop />;
}

// ─── ExploreDesktop ───────────────────────────────────────────────────────────

/**
 * [목적] 발견 데스크톱 레이아웃 — 추천 폴리오 섹션 + 필터 모달 + sort select + 공식 컬렉션 + 무한스크롤
 * [이유] design-align-v2: 추천 폴리오 reason badge 섹션 추가. 필터 모달 배선.
 *         기존 W4 기능 전체 보존(회귀 금지).
 * [사이드 이팩트] filterModalOpen state 추가. 추천 폴리오 4건 슬라이스.
 * [수정 시 고려사항] 추천 폴리오는 allFolios 앞 4건 슬라이스(실 recommendation API 연결 전 임시).
 */
function ExploreDesktop(): JSX.Element {
  const navigate = useNavigate();
  const { viewport, openSidebar } = useShell();
  const exploreFilters = useExploreFilters();
  // [2026-09-17] 카테고리 칩은 URL 필터(tags/duration)에서 파생 — 서버 조회와 동일 상태(useExploreFilters CHIP_FILTERS)
  const filter = exploreFilters.chip;
  const setFilter = exploreFilters.setChip;
  // [목적] 검색 입력 로컬 버퍼 — 300ms 디바운스 후 URL 쿼리(exploreFilters.setQ)에 반영
  // [이유] 키 입력마다 URL 갱신 + BE 재조회가 발생하면 타이핑이 끊겨 보이고 불필요한 요청이 급증한다.
  // [사이드 이팩트] exploreFilters.q(URL) 가 외부에서 바뀌면(초기화 버튼 등) qInput 도 동기화.
  const [qInput, setQInput] = useState(exploreFilters.q);
  useEffect(() => setQInput(exploreFilters.q), [exploreFilters.q]);
  // [수정: 2026-09-21] URL q 와 이미 같으면 setQ 를 호출하지 않는다.
  // [이유] deps 가 [qInput] 뿐이라 이 타이머는 마운트 시점의 exploreFilters(=칩 선택 전 빈 쿼리)를 클로저로 잡는다.
  //   setSearchParams 함수형 업데이터는 렌더 시점 searchParams 기준이라, 마운트 300ms 안에 칩을 눌러 생긴
  //   ?tags= 를 타이머 발화 시점에 옛 쿼리로 덮어써 칩 선택이 "전체"로 되돌아갔다(CI 플레이크로 발현).
  //   마운트 직후 q 가 그대로(qInput === URL q)면 쓸 값이 없으므로 타이머 자체를 만들지 않는다.
  // [사이드 이팩트] 없음 — 값이 같으면 update({q}) 는 어차피 동일 URL 로의 replace 였다.
  // [수정 시 고려사항] 타이핑 직후 300ms 안에 칩을 누르는 경우는 여전히 옛 클로저가 발화한다(qInput 이 달라 가드를
  //   통과). 완전히 막으려면 useExploreFilters.update 가 최신 params(ref)를 기준으로 계산하도록 바꿀 것.
  useEffect(() => {
    if (qInput === exploreFilters.q) return undefined;
    const t = setTimeout(() => exploreFilters.setQ(qInput), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  // [목적] 카테고리 칩 10종 — FilterModal.tsx 헤더 주석 참조(서버 미연결, 시각적 토글만 유지)
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const {
    data: collectionData,
    isLoading: collectionLoading,
    isError: collectionError,
    refetch: refetchCollections,
  } = useQuery({
    queryKey: queryKeys.collection.list({ kind: 'official', limit: 12 }),
    queryFn: () => collectionClient.list({ kind: 'official', limit: 12 }),
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });
  const officialCollections = collectionData?.items ?? [];

  // M7 P1: q/tags/duration/sort 를 BE 로 직전달 — 클라이언트 필터 제거
  const { allFolios, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error, refetch } =
    useFolioFeed({
      sort: exploreFilters.sort,
      q: exploreFilters.q || undefined,
      tags: exploreFilters.tags,
      minDurationDays: exploreFilters.minDurationDays,
      maxDurationDays: exploreFilters.maxDurationDays,
    });

  const cols = viewport.isDesktop ? 4 : viewport.isTablet ? 3 : 2;

  const filtered = allFolios;

  // M7 P1: 추천 폴리오 — 활성 필터(태그/기간)와 실제로 매칭되는 근거가 있을 때만 노출(mock 배지 제거)
  const hasActiveFilter = exploreFilters.tags.length > 0 || exploreFilters.duration !== '전체';
  const recommendedFolios = hasActiveFilter
    ? allFolios
        .map((f) => ({ folio: f, reason: buildMatchReason(f, exploreFilters) }))
        .filter((x): x is { folio: Folio; reason: string } => x.reason !== null)
        .slice(0, 4)
    : [];

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <>
      <TopBar title="발견" mobile={viewport.isMobile} onBurger={openSidebar} />
      <Box
        sx={{
          p: {
            xs: `${tokens.spacing[6]}px ${tokens.spacing[4]}px 60px`,
            md: `${tokens.spacing[8]}px ${tokens.spacing[8]}px 60px`,
          },
          maxWidth: 1320,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* [목적] 히어로 — 배경없는 텍스트 3줄 → 브랜드 틴트 그라데이션 카드
            [이유] 2026-09-20 explore-visual-pass — 상단에 시각적 앵커가 없어 페이지가 비어 보였다.
                   검색·필터·칩까지 한 카드 안으로 묶어 "탐색 입구"라는 역할을 명확히 한다.
            [사이드 이팩트] 없음 — 내부 요소의 DOM 구조·aria 라벨은 전부 기존과 동일(테스트 호환).
            [수정 시 고려사항] ::after 장식은 pointer-events:none 필수 — 안 그러면 검색창 클릭을 가로채다. */}
        <Box
          component="section"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            background: HERO_GRADIENT,
            border: `1px solid ${BORDER}`,
            borderRadius: `${tokens.radius.xl}px`,
            p: { xs: '24px 20px 20px', md: '34px 32px 26px' },
            mb: '26px',
            '&::after': {
              content: '""',
              position: 'absolute',
              right: -90,
              top: -120,
              width: 420,
              height: 420,
              borderRadius: '50%',
              background:
                'radial-gradient(circle at 35% 35%, rgba(31,111,235,.14), rgba(43,182,115,.10) 45%, transparent 70%)',
              pointerEvents: 'none',
            },
            '& > *': { position: 'relative', zIndex: 1 },
          }}
        >
          <Typography
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              bgcolor: 'rgba(31,111,235,.10)',
              color: BRAND_TEXT,
              borderRadius: 99,
              px: '12px',
              py: '5px',
              fontSize: 10.5,
              fontWeight: 800,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              letterSpacing: '0.14em',
              mb: '12px',
            }}
          >
            EXPLORE
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontSize: viewport.isMobile ? 24 : 38,
              fontWeight: 800,
              color: INK_HI,
              letterSpacing: '-0.9px',
              m: 0,
              mb: '10px',
              lineHeight: 1.22,
            }}
          >
            다른 사람들의 강원, 둘러보기
          </Typography>
          <Typography sx={{ fontSize: 14.5, color: INK_MID, m: 0, maxWidth: 520 }}>
            마음에 드는 폴리오는 Fork해서 내 폴리오로 가져올 수 있어요.
          </Typography>

          {/* 검색 + ⚙ 필터 버튼 — 히어로 내부로 편입, 폭을 480px 고정 → 유연하게 */}
          <Stack
            direction="row"
            spacing="10px"
            sx={{ mt: '22px', mb: '16px', flexWrap: 'wrap', rowGap: '10px' }}
          >
            <Box
              sx={{
                flex: '1 1 auto',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                bgcolor: PAPER0,
                border: `1px solid ${BORDER}`,
                borderRadius: `${tokens.radius.l}px`,
                px: '16px',
                height: 50,
                minWidth: 220,
                boxShadow: '0 2px 10px rgba(41,38,27,.05)',
                transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                '&:focus-within': {
                  borderColor: BRAND,
                  boxShadow: '0 0 0 3px rgba(31,111,235,.13)',
                },
              }}
            >
              <Box component="span" aria-hidden="true" sx={{ fontSize: 15, color: INK_LOW }}>
                ⌕
              </Box>
              <InputBase
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="제목·태그·키워드 검색"
                inputProps={{ 'aria-label': '폴리오 검색' }}
                sx={{ flex: 1, fontSize: 14, color: INK_HI }}
              />
              {qInput && (
                <ButtonBase
                  onClick={() => setQInput('')}
                  aria-label="검색어 지우기"
                  sx={{
                    color: INK_LOW,
                    fontSize: 13,
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                  }}
                >
                  ×
                </ButtonBase>
              )}
            </Box>

            {/* ⚙ 필터 버튼 — 클릭 시 FilterModal 열기. aria-expanded 로 모달 열림 상태 노출(WCAG 4.1.2, 2026-09-17 누락 수정) */}
            <ButtonBase
              onClick={() => setFilterModalOpen(true)}
              aria-label="필터 열기"
              aria-haspopup="dialog"
              aria-expanded={filterModalOpen}
              sx={{
                bgcolor: PAPER0,
                color: INK_HI,
                border: `1px solid ${BORDER}`,
                borderRadius: `${tokens.radius.l}px`,
                px: '18px',
                height: 50,
                flex: '0 0 auto',
                fontSize: 13,
                fontWeight: 700,
                gap: '7px',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 2px 10px rgba(41,38,27,.05)',
                transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                '&:hover': { borderColor: BRAND, color: BRAND_TEXT },
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              <Box component="span" aria-hidden="true">
                ⚙
              </Box>
              <Box component="span">필터</Box>
            </ButtonBase>

            {/* 기존 inline 필터 토글 버튼 보존 */}
            <ButtonBase
              onClick={() => setFilterOpen((v) => !v)}
              aria-label="필터칩 토글"
              aria-expanded={filterOpen}
              sx={{
                bgcolor: filterOpen ? INK_HI : PAPER0,
                color: filterOpen ? PAPER0 : INK_HI,
                border: `1px solid ${filterOpen ? INK_HI : BORDER}`,
                borderRadius: `${tokens.radius.l}px`,
                px: '18px',
                height: 50,
                flex: '0 0 auto',
                fontSize: 13,
                fontWeight: 700,
                gap: '7px',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 2px 10px rgba(41,38,27,.05)',
                transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                '&:hover': { borderColor: filterOpen ? INK_HI : BRAND },
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              <Box component="span" aria-hidden="true">
                {filterOpen ? '▲' : '▼'}
              </Box>
              <Box component="span">분류</Box>
            </ButtonBase>
          </Stack>

          {/* [목적] 필터칩 10종
              [이유] 2026-09-20: 기존에는 filterOpen true/false 분기가 완전히 동일한 마크업을 중복 선언하고
                     있었다(붙여넣기 잔류 — 칩은 어느 쌍이든 항상 렌더됨). 한 번만 렌더하도록 정리.
              [사이드 이팩트] 없음 — 동작·aria 동일. 분류 버튼(filterOpen)은 상태 표시 역할만 남는다.
              [수정 시 고려사항] 칩을 접을 수 있게 만들 거면 여기에 filterOpen 조건을 다시 넣되, 그때는
                                두 분기를 복사하지 말고 래퍼 하나에 조건만 걸 것. */}
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <ButtonBase
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  aria-label={`필터 ${f.label}`}
                  aria-pressed={active}
                  sx={{
                    bgcolor: active ? INK_HI : PAPER0,
                    color: active ? PAPER0 : INK_MID,
                    border: `1px solid ${active ? INK_HI : BORDER}`,
                    borderRadius: 99,
                    px: '15px',
                    height: 36,
                    fontSize: 12.5,
                    fontWeight: 600,
                    boxShadow: active ? '0 3px 10px rgba(41,38,27,.22)' : 'none',
                    transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                    '&:hover': active
                      ? {}
                      : { borderColor: BRAND, color: BRAND_TEXT, transform: 'translateY(-1px)' },
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                  }}
                >
                  {f.label}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>

        {/* ── 추천 폴리오 섹션 ────────────────────────────────────────────── */}
        {!isLoading && recommendedFolios.length > 0 && (
          <Box component="section" aria-label="추천 폴리오" sx={{ mb: '28px' }}>
            <Box sx={{ mb: '10px' }}>
              <Typography
                component="h3"
                sx={{ fontSize: 13, fontWeight: 700, color: INK_HI, m: 0, mb: '2px' }}
              >
                추천 폴리오
              </Typography>
              <Typography sx={{ fontSize: 11, color: INK_LOW }}>
                내 취향과 인기를 분석해 골랐어요
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(recommendedFolios.length, cols)}, 1fr)`,
                gap: '14px',
              }}
            >
              {recommendedFolios.map(({ folio, reason }) => (
                <RecommendedFolioCard
                  key={folio.id}
                  folio={folio}
                  reason={reason}
                  onClick={() => navigate(`/folio/${folio.id}`)}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* 공식 컬렉션 섹션 — [G1-2] 실패 시 조용히 숨기지 않고 InlineErrorState(재시도) 노출 */}
        {collectionError && (
          <Box component="section" sx={{ mb: '28px' }}>
            <InlineErrorState
              message="공식 컬렉션을 불러오지 못했습니다."
              onRetry={refetchCollections}
            />
          </Box>
        )}
        {!collectionError && (collectionLoading || officialCollections.length > 0) && (
          <Box component="section" sx={{ mb: '28px' }}>
            <Typography
              component="h2"
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: INK_HI,
                mb: '10px',
                letterSpacing: '-0.2px',
              }}
            >
              공식 컬렉션
            </Typography>
            {collectionLoading ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}>
                {[...Array(6)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{ borderRadius: `${tokens.radius.m}px`, minHeight: 78, bgcolor: PAPER2 }}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${Math.min(officialCollections.length, 6)}, 1fr)`,
                  gap: '8px',
                }}
              >
                {officialCollections.map((col: Collection, idx: number) => (
                  <ButtonBase
                    key={col.id}
                    onClick={() => navigate(`/collections/${col.id}`)}
                    aria-label={`공식 컬렉션 ${col.title}`}
                    sx={{
                      bgcolor: COLLECTION_BG_CYCLE[idx % COLLECTION_BG_CYCLE.length],
                      borderRadius: `${tokens.radius.m}px`,
                      p: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      textAlign: 'left',
                      minHeight: 78,
                      '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                    }}
                  >
                    <Box component="span" aria-hidden="true" sx={{ fontSize: 18, mb: '5px' }}>
                      🏛
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: INK_HI,
                        letterSpacing: '-0.2px',
                        lineHeight: 1.3,
                      }}
                    >
                      {col.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 9.5,
                        color: INK_MID,
                        mt: '3px',
                        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                      }}
                    >
                      {col.itemCount} folios
                    </Typography>
                  </ButtonBase>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* 모든 폴리오 — auto-fill CSS 그리드 (2026-09-20: 컬럼 메이슨리에서 전환) */}
        <Box component="section">
          {/* [목적] 섹션 헤더 — 제목 + 결과수 pill + 구분선 + 정렬
              [이유] 2026-09-20 explore-visual-pass — 기존 space-between 은 제목과 정렬 셀렉트를 1200px 이상
                     벌려놓아 서로 무관한 UI 처럼 보였고, 결과 개수는 다시 그 아래 단독 줄에 떠 있어
                     세 조각이 전부 따로 놀았다. 제목+개수를 붙이고 구분선으로 정렬까지 한 줄에 묶는다.
              [사이드 이팩트] 개수 표시가 단독 줄 → 헤더 내 pill 로 이동("N개" 텍스트 자체는 동일 유지).
              [수정 시 고려사항] 구분선은 flex:1 빈 Box — 제목·pill·Select 은 flex:0 0 auto 여야 밀리지 않는다. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              mb: '6px',
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: 20,
                fontWeight: 800,
                color: INK_HI,
                letterSpacing: '-0.4px',
                m: 0,
                flex: '0 0 auto',
              }}
            >
              모든 폴리오
            </Typography>
            {!isLoading && (
              <Typography
                sx={{
                  flex: '0 0 auto',
                  bgcolor: SOFT_LINE,
                  color: INK_LOW,
                  fontSize: 11.5,
                  fontWeight: 700,
                  px: '10px',
                  py: '4px',
                  borderRadius: 99,
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                }}
              >
                {filtered.length}개
              </Typography>
            )}
            <Box aria-hidden="true" sx={{ flex: '1 1 auto', height: '1px', bgcolor: SOFT_LINE }} />
            <Select
              value={exploreFilters.sort}
              onChange={(e) => exploreFilters.setSort(e.target.value as FolioFeedSort)}
              size="small"
              inputProps={{ 'aria-label': '정렬 기준 선택' }}
              sx={{
                bgcolor: PAPER0,
                border: `1px solid ${BORDER}`,
                borderRadius: `${tokens.radius.s}px`,
                fontSize: 11.5,
                color: INK_HI,
                fontFamily: 'Pretendard, sans-serif',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: 12, color: INK_HI }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {Boolean(error) && !isLoading && (
            <InlineErrorState message="데이터를 불러오지 못했습니다." onRetry={refetch} />
          )}

          {isLoading && (
            <Box sx={FEED_GRID_SX}>
              {[...Array(8)].map((_, i) => (
                <FolioCardSkeleton key={i} />
              ))}
            </Box>
          )}

          {!isLoading && filtered.length > 0 && (
            <Box sx={FEED_GRID_SX}>
              {filtered.map((f) => (
                <ExFolioCard key={f.id} folio={f} onClick={() => navigate(`/folio/${f.id}`)} />
              ))}
              {/* [목적] 결과가 한 줄을 못 채울 때 남는 빈 칸을 CTA 타일로 메꾸기
                  [이유] 2026-09-20: 폴리오가 1개일 때 4열 그리드의 나머지 3칸이 통째로 비어
                         화면이 고장난 것처럼 보였다. 빈 칸을 "다음 행동"으로 치환한다.
                  [사이드 이팩트] hasNextPage 가 있어도 현재 로드된 개수 기준으로 판정 — 무한스크롤로
                                 항목이 늘면 자연스럽게 사라진다.
                  [수정 시 고려사항] 0건은 아래 전용 빈 상태 UI 가 담당 — 여기서 중복 노출 금지. */}
              {filtered.length < SPARSE_FEED_THRESHOLD && (
                <CreateFolioTile onClick={() => navigate('/new-folio')} />
              )}
            </Box>
          )}

          {/* [목적] 결과 0건 빈 상태 — 검색어/필터 때문인지 구분하고 해제 경로를 준다
              [이유] 2026-09-18 QA(M-8): 태그·기간 필터로 0건이 나와도 "아직 폴리오가 없습니다."
                     라고만 떠서 서비스 전체가 비어 있는 것처럼 읽혔고, 되돌릴 버튼도 없었다.
              [사이드 이팩트] 필터가 걸린 경우에만 reset 버튼이 노출된다(진짜 빈 목록엔 미노출).
              [수정 시 고려사항] 필터 항목이 늘어나면 hasActiveFilter 조건에 함께 추가할 것. */}
          {!isLoading && filtered.length === 0 && !error && (
            <Box sx={{ textAlign: 'center', p: '60px 20px' }}>
              <Typography sx={{ fontSize: 13, color: INK_MID }}>
                {exploreFilters.q
                  ? '검색 결과가 없습니다.'
                  : hasActiveFilter
                    ? '조건에 맞는 폴리오가 없습니다.'
                    : '아직 폴리오가 없습니다.'}
              </Typography>
              {(exploreFilters.q || hasActiveFilter) && (
                <ButtonBase
                  onClick={() => exploreFilters.reset()}
                  sx={{
                    mt: `${tokens.spacing[3]}px`,
                    px: `${tokens.spacing[4]}px`,
                    py: `${tokens.spacing[2]}px`,
                    fontSize: 12,
                    fontWeight: 600,
                    color: BRAND,
                    border: `1px solid ${BORDER}`,
                    borderRadius: `${tokens.radius.xl}px`,
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
                  }}
                >
                  필터 초기화
                </ButtonBase>
              )}
            </Box>
          )}

          <Box ref={sentinelRef} sx={{ height: 1 }} aria-hidden="true" />

          {isFetchingNextPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: '20px' }}>
              <CircularProgress size={24} aria-label="더 불러오는 중" />
            </Box>
          )}

          {!hasNextPage && allFolios.length > 0 && !isLoading && (
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: 11,
                color: INK_LOW,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                py: '16px',
              }}
            >
              — 전체 {allFolios.length}개 —
            </Typography>
          )}
        </Box>

        {/* [목적] 테마 탐색 섹션 + 하단 CTA 배너 — 피드 아래 여백을 다음 행동으로 채운다
            [이유] 2026-09-20 explore-visual-pass — 폴리오 수가 적은 초기 서비스에서 피드 아래가
                   뷰포트 절반 이상 통째로 비어 서비스 자체가 빈 것처럼 읽혔다.
            [사이드 이팩트] 테마 타일은 exploreFilters.setChip 을 호출 — 기존 필터칩과 완전히 동일한
                           경로(URL 쿼리 갱신 → 서버 재조회)라 상태 불일치가 생기지 않는다.
            [수정 시 고려사항] 필터가 걸려 0건이 된 상황에서도 탐색 섹션은 그대로 두는 게 유용하다
                              (다른 테마로 빠져나갈 경로 제공) — 조건부 숨김 추가하지 말 것. */}
        <ThemeExploreSection onSelect={setFilter} />
        <ChatCtaBanner onClick={() => navigate('/chat')} />
      </Box>

      {/* 필터 모달 */}
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        duration={exploreFilters.duration}
        onDurationChange={exploreFilters.setDuration}
        tags={exploreFilters.tags}
        onTagsChange={exploreFilters.setTags}
        onReset={exploreFilters.reset}
        resultCount={filtered.length}
      />
    </>
  );
}

// ─── buildMatchReason ─────────────────────────────────────────────────────────

/**
 * [목적] 추천 폴리오 reason badge 텍스트 — 활성 필터(테마 태그/여행 기간)와의 실제 매칭 근거로 생성
 * [이유] M7 P1: mock 텍스트("내 취향 저격" 등) 제거 — 근거 없으면 null 반환(호출부가 배지/카드 자체를 숨김).
 * [사이드 이팩트] 없음(순수 함수). tags/minDurationDays/maxDurationDays 는 이미 서버 조회 단계에서
 *               필터링되어 있으므로 folio 는 항상 조건을 만족 — 여기서는 "어떤 근거인지"만 조립한다.
 * [수정 시 고려사항] 태그 근거를 기간 근거보다 우선 표시(더 구체적인 정보로 간주).
 */
function buildMatchReason(
  folio: Folio,
  filters: Pick<ReturnType<typeof useExploreFilters>, 'tags' | 'duration'>,
): string | null {
  if (filters.tags.length > 0) {
    const matched = folio.vibe.find((v) => filters.tags.includes(v));
    if (matched) return `#${matched} 태그 일치`;
  }
  if (filters.duration !== '전체' && folio.duration.days != null) {
    return `${filters.duration} 일정 조건 일치`;
  }
  return null;
}

// ─── RecommendedFolioCard ─────────────────────────────────────────────────────

/**
 * [목적] 추천 폴리오 카드 — 기존 ExFolioCard 에 reason badge 오버레이 추가
 * [이유] design-align-v2 추천 섹션 — 카드 좌상단 reason pill badge.
 *         M7 P1: reason 은 buildMatchReason(실제 활성 필터 매칭 근거) — mock 텍스트/색상 순환 제거,
 *         배지 색은 BRAND 단일 톤으로 고정.
 * [사이드 이팩트] ExFolioCard 재사용하지 않고 별도 컴포넌트 — badge 위치 오버레이 필요
 * [수정 시 고려사항] reason 이 null 이면 호출부(recommendedFolios 필터)가 이미 걸러내므로 항상 string.
 */
function RecommendedFolioCard({
  folio,
  reason,
  onClick,
}: {
  folio: Folio;
  reason: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`추천 폴리오 ${folio.title} 열기`}
      sx={{
        display: 'block',
        width: '100%',
        bgcolor: PAPER0,
        borderRadius: `${tokens.radius.m}px`,
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        textAlign: 'left',
        transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
        boxShadow: tokens.shadow.card,
        '&:hover': { transform: 'translateY(-2px)', boxShadow: tokens.shadow.modal },
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      {/* 커버 + reason badge overlay */}
      <Box
        sx={{
          height: 130,
          bgcolor: PAPER2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 40,
          position: 'relative',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        {folio.coverImageUrl ? (
          <Box
            component="img"
            src={folio.coverImageUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          '🗺'
        )}

        {/* reason badge — 좌상단 pill */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            bgcolor: BRAND,
            color: PAPER0,
            borderRadius: '6px',
            px: '8px',
            py: '3px',
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '-0.1px',
            lineHeight: 1.4,
            maxWidth: '80%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          aria-label={`추천 이유: ${reason}`}
        >
          {reason}
        </Box>

        {folio.duration.days && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(255,255,255,.95)',
              borderRadius: `${tokens.radius.s}px`,
              px: '8px',
              py: '3px',
              fontSize: 10,
              color: INK_HI,
              fontWeight: 700,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {folio.duration.days}일
          </Box>
        )}
      </Box>

      {/* 본문 */}
      <Box sx={{ p: '12px 13px' }}>
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            color: INK_HI,
            lineHeight: 1.4,
            mb: '7px',
            letterSpacing: '-0.2px',
          }}
        >
          {folio.title}
        </Typography>

        {folio.vibe.length > 0 && (
          <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', mb: '8px' }}>
            {folio.vibe.slice(0, 3).map((v) => (
              <Box
                key={v}
                component="span"
                sx={{
                  fontSize: 10,
                  bgcolor: PAPER2,
                  color: INK_MID,
                  borderRadius: `${tokens.radius.s}px`,
                  px: '6px',
                  py: '2px',
                  fontWeight: 500,
                }}
              >
                #{v}
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 10.5,
            color: INK_LOW,
          }}
        >
          <Box component="span" sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
            {folioAuthorLabel(folio.author)}
          </Box>
          <FolioLikeChip folio={folio} />
        </Box>
      </Box>
    </ButtonBase>
  );
}

// ─── ExFolioCard ──────────────────────────────────────────────────────────────

/**
 * [목적] Explore 피드 폴리오 카드 — 실데이터(Folio) 기반 렌더링
 * [이유] FEED-04 — mock 제거 후 실 Folio 타입으로 교체. W4 회귀 금지.
 *         fe-folio-social-wire: FolioLikeChip 으로 좋아요 실배선(stopPropagation 으로 카드 navigate 와 분리).
 * [사이드 이팩트] 없음 — FolioLikeChip 이 자체 캐시(queryKeys.folioSocial.likeState)로 상태 관리.
 * [수정 시 고려사항] 2026-09-20 explore-visual-pass 로 부모가 CSS grid(FEED_GRID_SX) 가 되면서
 *                   breakInside:'avoid' / mb 는 제거되었다(gap 이 간격을 책임진다).
 *                   컬럼 메이슨리로 되돌릴 경우에만 둘 다 함께 복구할 것.
 */
// [목적] 피드 카드 커버 고정 높이
// [이유] 2026-09-20 explore-visual-pass — 기존에는 `100 + (title.length % 5) * 20` 으로 제목 길이에 따라
//        커버 높이를 흔들어 컬럼 메이슨리 느낌을 내었다. CSS grid 로 전환한 뒤에는 같은 행의
//        카드들만 들쭉날쭉해 보여 이득이 없다.
// [사이드 이팩트] 카드 높이가 균일해진다.
// [수정 시 고려사항] 값을 바꾸면 워터마크 폰트 크기(104px)도 같이 재조정해야 비율이 유지된다.
const CARD_COVER_HEIGHT = 172;

function ExFolioCard({ folio, onClick }: { folio: Folio; onClick: () => void }): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`폴리오 ${folio.title} 열기`}
      sx={{
        display: 'block',
        width: '100%',
        bgcolor: PAPER0,
        borderRadius: `${tokens.radius.l}px`,
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        textAlign: 'left',
        transition: `all ${tokens.motion.med.duration}ms ${tokens.motion.med.easing}`,
        boxShadow: tokens.shadow.card,
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: tokens.shadow.modal,
          borderColor: BRAND,
        },
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          height: CARD_COVER_HEIGHT,
          background: folio.coverImageUrl ? PAPER2 : CARD_COVER_GRADIENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        {folio.coverImageUrl ? (
          <Box
            component="img"
            src={folio.coverImageUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          // 커버 미설정 플레이스홀더 — 그라데이션 위 큰 워터마크(장식이므로 aria-hidden 부모 안에 위치)
          <Box component="span" sx={{ fontSize: 104, opacity: 0.22, lineHeight: 1 }}>
            🗺
          </Box>
        )}
        {folio.duration.days && (
          <Box
            sx={{
              position: 'absolute',
              top: '9px',
              left: '9px',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,.95)',
              boxShadow: '0 1px 4px rgba(41,38,27,.10)',
              borderRadius: `${tokens.radius.s}px`,
              px: '8px',
              py: '3px',
              fontSize: 10,
              color: INK_HI,
              fontWeight: 700,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {folio.duration.days}일
          </Box>
        )}
      </Box>

      <Box sx={{ p: '12px 13px' }}>
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            color: INK_HI,
            lineHeight: 1.4,
            mb: '7px',
            letterSpacing: '-0.2px',
          }}
        >
          {folio.title}
        </Typography>

        {folio.vibe.length > 0 && (
          <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', mb: '8px' }}>
            {folio.vibe.slice(0, 3).map((v) => (
              <Box
                key={v}
                component="span"
                sx={{
                  fontSize: 10,
                  bgcolor: PAPER2,
                  color: INK_MID,
                  borderRadius: `${tokens.radius.s}px`,
                  px: '6px',
                  py: '2px',
                  fontWeight: 500,
                }}
              >
                #{v}
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 10.5,
            color: INK_LOW,
          }}
        >
          <Box component="span" sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
            {folioAuthorLabel(folio.author)}
          </Box>
          <FolioLikeChip folio={folio} />
        </Box>
      </Box>
    </ButtonBase>
  );
}

// ─── CreateFolioTile ──────────────────────────────────────────────────────────

/**
 * [목적] 피드 그리드의 남는 빈 칸을 채우는 "새 폴리오 만들기" CTA 타일
 * [이유] 2026-09-20 explore-visual-pass — 폴리오가 1개면 4열 auto-fill 그리드의 나머지 3칸이
 *        통째로 비어 화면이 망가진 것처럼 보였다. 빈 칸을 방치하는 대신 다음 행동을 제안한다.
 * [사이드 이팩트] 클릭 시 /new-folio 로 이동(호출부가 onClick 주입 — 라우팅 지식을 컴포넌트에 넣지 않음).
 * [수정 시 고려사항] 실제 폴리오 카드와 혼동되면 안 되므로 점선 보더 + 반투명 브랜드 틴트로
 *                   "비어 있는 자리"임을 시각적으로 구분한다. 실선 카드로 바꾸지 말 것.
 */
function CreateFolioTile({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label="새 폴리오 만들기"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        textAlign: 'left',
        border: `1.5px dashed ${BORDER}`,
        borderRadius: `${tokens.radius.l}px`,
        bgcolor: 'rgba(31,111,235,.035)',
        overflow: 'hidden',
        transition: `all ${tokens.motion.med.duration}ms ${tokens.motion.med.easing}`,
        '&:hover': { borderColor: BRAND, bgcolor: 'rgba(31,111,235,.07)' },
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          height: CARD_COVER_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            bgcolor: BRAND,
            color: PAPER0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            fontWeight: 300,
            lineHeight: 1,
          }}
        >
          +
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND_TEXT }}>
          새 폴리오 만들기
        </Typography>
      </Box>
      <Box sx={{ p: '14px 15px 13px' }}>
        <Typography
          sx={{
            fontSize: 14.5,
            fontWeight: 700,
            color: INK_HI,
            letterSpacing: '-0.3px',
            mb: '7px',
          }}
        >
          내 강원 여행도 공유해보세요
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: INK_LOW, lineHeight: 1.5 }}>
          대화만으로 일정이 만들어져요
        </Typography>
      </Box>
    </ButtonBase>
  );
}

// ─── ThemeExploreSection ──────────────────────────────────────────────────────

/**
 * [목적] "이런 테마는 어때요?" — 테마 타일 6종으로 피드 아래 여백을 탐색 진입점으로 전환
 * [이유] 2026-09-20 explore-visual-pass — 초기 서비스(폴리오 소수)에서 피드 아래가 뷰포트 절반 이상
 *        통째로 비어 서비스 자체가 빈 것처럼 읽혔다.
 * [사이드 이팩트] 타일 클릭 → onSelect(chipId) → exploreFilters.setChip → URL 쿼리 갱신 → 서버 재조회.
 *                 즉 상단 필터칩을 누른 것과 완전히 동일한 경로라 상태 불일치가 발생하지 않는다.
 * [수정 시 고려사항] THEME_TILES 의 chipId 는 useExploreFilters.CHIP_FILTERS 키여야 한다 —
 *                   없는 키를 넣으면 setChip 이 필터를 전체 해제해버려 조용히 오동작한다.
 */
function ThemeExploreSection({ onSelect }: { onSelect: (chipId: string) => void }): JSX.Element {
  return (
    <Box component="section" aria-label="테마로 둘러보기" sx={{ mt: '44px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', mb: '4px' }}>
        <Typography
          component="h2"
          sx={{
            fontSize: 20,
            fontWeight: 800,
            color: INK_HI,
            letterSpacing: '-0.4px',
            m: 0,
            flex: '0 0 auto',
          }}
        >
          이런 테마는 어때요?
        </Typography>
        <Box aria-hidden="true" sx={{ flex: '1 1 auto', height: '1px', bgcolor: SOFT_LINE }} />
      </Box>
      <Typography sx={{ fontSize: 12.5, color: INK_LOW, mb: '18px' }}>
        관심 있는 테마를 누르면 바로 필터가 적용돼요
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
          gap: '14px',
        }}
      >
        {THEME_TILES.map((t) => (
          <ButtonBase
            key={t.chipId}
            onClick={() => onSelect(t.chipId)}
            aria-label={`테마 ${t.label} 둘러보기`}
            sx={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              bgcolor: PAPER0,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.l}px`,
              overflow: 'hidden',
              transition: `all ${tokens.motion.med.duration}ms ${tokens.motion.med.easing}`,
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: tokens.shadow.modal,
                borderColor: BRAND,
              },
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                height: 96,
                background: t.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 40,
              }}
            >
              {t.emoji}
            </Box>
            <Box sx={{ p: '11px 13px 12px' }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK_HI, mb: '3px' }}>
                {t.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: INK_LOW }}>{t.desc}</Typography>
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}

// ─── ChatCtaBanner ────────────────────────────────────────────────────────────

/**
 * [목적] 페이지 하단 마감 배너 — 찾는 여행이 없을 때 대화형 생성(/chat)으로 유도
 * [이유] 2026-09-20 explore-visual-pass — 페이지가 "— 전체 N개 —" 로 끊기고 끝나 마무리가 허전했다.
 * [사이드 이팩트] 클릭 시 호출부가 주입한 onClick(/chat 이동) 실행.
 * [수정 시 고려사항] 배경이 brand→accent[600] 그라데이션이므로 위에 얹는 텍스트는 흰색 고정.
 *                   accent[500] 로 바꾸면 흰 텍스트 대비가 AA 미달(2.61:1)이 되므로 600 유지 필수
 *                   (packages/ui tokens/color.ts accent[600] 주석 참조).
 */
function ChatCtaBanner({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label="대화로 폴리오 만들기"
      sx={{
        mt: '40px',
        width: '100%',
        p: { xs: '24px 20px', md: '30px 32px' },
        borderRadius: `${tokens.radius.l}px`,
        background: `linear-gradient(120deg, ${BRAND} 0%, ${ACCENT_TEXT_BG} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px',
        flexWrap: 'wrap',
        textAlign: 'left',
        '&:focus-visible': { outline: `2px solid ${INK_HI}`, outlineOffset: 3 },
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: 19,
            fontWeight: 800,
            color: PAPER0,
            letterSpacing: '-0.4px',
            mb: '6px',
          }}
        >
          아직 찾는 여행이 없나요?
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,.88)' }}>
          가고 싶은 곳을 말하면 강원캔버스가 일정을 그려드려요.
        </Typography>
      </Box>
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          bgcolor: PAPER0,
          color: BRAND_TEXT,
          fontSize: 13,
          fontWeight: 800,
          px: '22px',
          py: '12px',
          borderRadius: 99,
          whiteSpace: 'nowrap',
        }}
      >
        대화로 만들기 →
      </Box>
    </ButtonBase>
  );
}
